import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Upload as UploadIcon, FileText, User, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { CURRICULA, getSubjectNamesForCurriculum, getCurriculum } from '../constants/curricula';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Upload = () => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [curriculum, setCurriculum] = useState('');
  const [level, setLevel] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [uploaderName, setUploaderName] = useState('');
  const [postAnonymously, setPostAnonymously] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  const currObj = getCurriculum(curriculum);
  const needsLevel = currObj?.levels;
  const subjectOptions = curriculum ? getSubjectNamesForCurriculum(curriculum) : [];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleCurriculumChange = (val) => {
    setCurriculum(val);
    const c = getCurriculum(val);
    if (!c?.levels) setLevel('');
    else setLevel('');
    // Reset subject because the available list may have changed (esp. IB)
    setSubject('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !title || !subject || !curriculum) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (needsLevel && !level) {
      toast.error(`Please choose a level for ${curriculum}`);
      return;
    }
    if (!postAnonymously && !uploaderName.trim()) {
      toast.error('Enter your name or toggle "Post anonymously"');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const finalName = postAnonymously ? 'Anonymous' : uploaderName.trim();
      const params = new URLSearchParams({
        title,
        subject,
        curriculum,
        description: description || '',
        tags: tags || '',
        uploader_name: finalName,
      });
      if (needsLevel && level) params.set('level', level);

      await axios.post(`${API}/resources/upload?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Resource uploaded successfully!');
      const browseParams = new URLSearchParams({ curriculum, subject });
      if (needsLevel && level) browseParams.set('level', level);
      navigate(`/resources?${browseParams.toString()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-semibold tracking-tight mb-2">Upload Resource</h1>
          <p className="text-foreground/75 leading-relaxed">Share your notes — no account needed.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="upload-form">
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center ${
              dragActive ? 'border-primary bg-accent' : 'border-border'
            } hover:border-primary transition-colors`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="upload-drop-zone"
          >
            {file ? (
              <div className="space-y-4">
                <FileText className="h-16 w-16 text-primary mx-auto" strokeWidth={1.5} />
                <div>
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-foreground/70">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <Button type="button" variant="outline" onClick={() => setFile(null)} data-testid="upload-remove-file">
                  Remove file
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-accent flex items-center justify-center">
                  <UploadIcon className="h-10 w-10 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-lg font-medium mb-2">Drag and drop your file here</p>
                  <p className="text-sm text-foreground/70 mb-4">PDF, DOCX, PNG, JPG (Max 10MB)</p>
                </div>
                <label htmlFor="file-input">
                  <Button type="button" asChild data-testid="upload-choose-file">
                    <span>
                      <UploadIcon className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Choose file
                    </span>
                  </Button>
                  <input
                    id="file-input"
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.doc,.png,.jpg,.jpeg"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Calculus Revision Notes"
                required
                className="mt-1"
                data-testid="upload-title-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="curriculum">Curriculum *</Label>
                <Select value={curriculum} onValueChange={handleCurriculumChange} required>
                  <SelectTrigger className="mt-1" data-testid="upload-curriculum-select">
                    <SelectValue placeholder="Choose curriculum" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRICULA.map((c) => (
                      <SelectItem key={c.name} value={c.name} data-testid={`upload-curriculum-option-${c.name.toLowerCase()}`}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {needsLevel ? (
                <div>
                  <Label htmlFor="level">Level *</Label>
                  <Select value={level} onValueChange={setLevel} required>
                    <SelectTrigger className="mt-1" data-testid="upload-level-select">
                      <SelectValue placeholder={`Choose ${currObj.levels.join(' or ')}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {currObj.levels.map((lvl) => (
                        <SelectItem key={lvl} value={lvl} data-testid={`upload-level-option-${lvl}`}>
                          {lvl}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="hidden sm:block" />
              )}
            </div>

            <div>
              <Label htmlFor="subject">Subject *</Label>
              <Select value={subject} onValueChange={setSubject} required disabled={!curriculum}>
                <SelectTrigger className="mt-1" data-testid="upload-subject-select">
                  <SelectValue placeholder={curriculum ? 'Select a subject' : 'Choose a curriculum first'} />
                </SelectTrigger>
                <SelectContent>
                  {subjectOptions.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`upload-subject-option-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide details about this resource..."
                rows={4}
                className="mt-1"
                data-testid="upload-description-input"
              />
            </div>

            <div>
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g., derivatives, integration, limits (comma-separated)"
                className="mt-1"
                data-testid="upload-tags-input"
              />
            </div>

            {/* Anonymous vs Named */}
            <div className="rounded-lg border border-border bg-surface p-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                  {postAnonymously ? (
                    <EyeOff className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  ) : (
                    <User className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex-1">
                  <Label htmlFor="anon-toggle" className="text-base font-medium cursor-pointer">
                    Post anonymously
                  </Label>
                  <p className="text-sm text-foreground/70 mt-0.5">
                    {postAnonymously
                      ? 'Your name won\'t be shown. Others will see "Anonymous".'
                      : 'Your name will be shown next to this resource.'}
                  </p>
                </div>
                <Checkbox
                  id="anon-toggle"
                  checked={postAnonymously}
                  onCheckedChange={(v) => setPostAnonymously(Boolean(v))}
                  data-testid="upload-anonymous-toggle"
                />
              </div>

              {!postAnonymously && (
                <div>
                  <Label htmlFor="uploader-name">Your name *</Label>
                  <Input
                    id="uploader-name"
                    value={uploaderName}
                    onChange={(e) => setUploaderName(e.target.value)}
                    placeholder="e.g., Alex"
                    required={!postAnonymously}
                    className="mt-1"
                    data-testid="upload-name-input"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={loading || !file}
              className="flex-1"
              data-testid="upload-submit-button"
            >
              {loading ? 'Uploading...' : 'Upload Resource'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              data-testid="upload-cancel-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
